import { Button, DeadlineNote, Icon } from '../../design-system/index.js'
import { HERO_HIGHLIGHT } from '../../content/landing'
import { wrap } from '../layout.js'

/**
 * Hero: a centred pitch over the ambient backdrop.
 *
 * It used to close on a still of the routes list; that list is gone, and so
 * is the picture of it. The tilted cards either side are the only imagery
 * left, and `landing.css` drops them below 1280px — so nothing they say may
 * be load-bearing.
 */
export function Hero({ t, desktop, authHref, signedIn = false }) {
  return (
    <section className="u-hero" aria-labelledby="hero-title">
      <div
        style={{
          ...wrap(desktop),
          position: 'relative',
          paddingTop: desktop ? 56 : 32,
          paddingBottom: desktop ? 72 : 40,
          textAlign: 'center',
        }}
      >
        <HeroDecoration t={t} />

        <p className="u-pill" style={{ margin: 0 }}>
          <span className="u-pill-dot" aria-hidden="true" />
          {t.eyebrow}
        </p>

        <h1
          id="hero-title"
          style={{
            fontSize: desktop ? 56 : 34,
            margin: desktop ? '22px auto 0' : '18px auto 0',
            maxWidth: 880,
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
          }}
        >
          {t.h1}
        </h1>

        <p
          style={{
            fontSize: desktop ? 19 : 16,
            color: 'var(--ink-2)',
            lineHeight: 1.55,
            margin: '18px auto 0',
            maxWidth: 620,
          }}
        >
          {t.sub}
        </p>

        <div
          style={{
            display: 'flex',
            gap: 12,
            marginTop: 28,
            justifyContent: 'center',
            flexDirection: desktop ? 'row' : 'column',
          }}
        >
          <Button size="lg" full={!desktop} href={authHref}>
            {signedIn ? t.openCabinet : t.cta}
          </Button>
          <Button size="lg" variant="secondary" full={!desktop} href="#how-it-works">
            {t.cta2}
          </Button>
        </div>

        <p
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            justifyContent: 'center',
            margin: '18px 0 0',
            fontSize: 14,
            color: 'var(--ink-2)',
            textAlign: 'left',
          }}
        >
          <span style={{ color: 'var(--accent-text)', display: 'inline-flex', flex: 'none' }}>
            <Icon name="shield-check" size={16} />
          </span>
          {t.trust}
        </p>
      </div>
    </section>
  )
}

/**
 * The tilted cards parked either side of the hero copy. Purely decorative —
 * `landing.css` removes them below 1280px, where there is no room beside the
 * text, so nothing they say may be load-bearing.
 */
function HeroDecoration({ t }) {
  return (
    <div aria-hidden="true">
      <div className="u-float" style={{ left: -56, top: 96, transform: 'rotate(-5deg)' }}>
        <div className="u-float-card">
          <div className="u-float-title">Erasmus Mundus</div>
          <div className="u-float-meta">EU · {t.fund}</div>
        </div>
      </div>

      <div className="u-float" style={{ left: -18, top: 268, transform: 'rotate(3deg)' }}>
        <DeadlineNote date="15 Jan" tilt={0} style={{ maxWidth: 200 }}>
          Stipendium Hungaricum
        </DeadlineNote>
      </div>

      <div className="u-float" style={{ right: -60, top: 128, transform: 'rotate(4deg)' }}>
        <div className="u-float-card">
          <div className="u-float-title">DAAD</div>
          <div className="u-float-meta">Germany · {t.verified}</div>
        </div>
      </div>

      <div className="u-float" style={{ right: -24, top: 288, transform: 'rotate(-4deg)' }}>
        <div className="u-float-card" style={{ minWidth: 150 }}>
          {HERO_HIGHLIGHT.requirements.map(([term, value], i) => (
            <div key={term} className={i === 0 ? 'u-float-title' : 'u-float-meta'}>
              {term} {value}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Hero
